import React from 'react';
import {
  Badge,
  Avatar,
  AvatarGroup,
  Button,
  HorizontalBarChart,
  AIIcon,
  Stack,
} from '@/components/ui';
import { Card, CardTitle, CardDescription, CardContent } from '@/components/ui/Card/Card';
import { CodeBlock } from '../CodeBlock';
import { SectionWrapper } from '../SectionWrapper';

export function DisplaySection() {
  return (
    <SectionWrapper title="Display Components">
        {/* Charts */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Charts</h3>
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <div className="p-4">
                <CardTitle className="text-base">Horizontal Bar Chart</CardTitle>
                <CardDescription>Use for simple ranked comparisons</CardDescription>
              </div>
              <CardContent className="px-4 pb-4">
                <HorizontalBarChart
                  unit="ms"
                  data={[
                    { label: 'embed_doc (42)', value: 820 },
                    { label: 'gallery_image_analysis (11)', value: 460 },
                    { label: 'org_profile_research (7)', value: 210 },
                  ]}
                />
              </CardContent>
            </Card>
          </div>
          <CodeBlock>{`import { HorizontalBarChart } from '@/components/ui';

<HorizontalBarChart
  unit="ms"
  data={[
    { label: 'embed_doc (42)', value: 820 },
    { label: 'gallery_image_analysis (11)', value: 460 },
    { label: 'org_profile_research (7)', value: 210 },
  ]}
/>`}
          </CodeBlock>
        </div>
        {/* Badges */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Badges</h3>
          <div className="flex flex-wrap gap-2">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="success">Success</Badge>
            <Badge variant="warning">Warning</Badge>
            <Badge variant="error">Error</Badge>
            <Badge variant="outline">Outline</Badge>
          </div>
          <CodeBlock>
{`<Badge>Default</Badge>
<Badge variant="secondary">Secondary</Badge>
<Badge variant="success">Success</Badge>
<Badge variant="warning">Warning</Badge>
<Badge variant="error">Error</Badge>
<Badge variant="outline">Outline</Badge>`}
          </CodeBlock>
        </div>

        {/* AI Icon */}
        <div>
          <h3 className="text-lg font-semibold mb-4">AI Icon</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Standardized icon for AI-powered features across the platform
          </p>
          <div className="space-y-4">
            {/* Icon Variants */}
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="text-sm font-semibold mb-3">Variants</h4>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <AIIcon className="w-8 h-8 mb-2" />
                  <span className="text-xs text-muted-foreground">Default</span>
                </div>
                <div className="text-center">
                  <AIIcon variant="primary" className="w-8 h-8 mb-2" />
                  <span className="text-xs text-muted-foreground">Primary</span>
                </div>
                <div className="text-center">
                  <AIIcon variant="muted" className="w-8 h-8 mb-2" />
                  <span className="text-xs text-muted-foreground">Muted</span>
                </div>
              </div>
            </div>

            {/* Icon in Buttons */}
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="text-sm font-semibold mb-3">In Buttons</h4>
              <div className="flex flex-wrap gap-3">
                <Button size="sm">
                  <AIIcon className="w-4 h-4 mr-2" />
                  Run Research
                </Button>
                <Button variant="outline" size="sm">
                  <AIIcon className="w-4 h-4 mr-2" />
                  AI Assist
                </Button>
                <Button variant="ghost" size="sm" className="hover:bg-accent-50">
                  <AIIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Different Sizes */}
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="text-sm font-semibold mb-3">Sizes</h4>
              <div className="flex items-end gap-4">
                <AIIcon className="w-4 h-4" />
                <AIIcon className="w-5 h-5" />
                <AIIcon className="w-6 h-6" />
                <AIIcon className="w-8 h-8" />
                <AIIcon className="w-10 h-10" />
              </div>
            </div>
          </div>
          <CodeBlock>
{`// Default variant
<AIIcon className="w-4 h-4" />

// Primary variant (active/prominent)
<AIIcon variant="primary" className="w-4 h-4" />

// Muted variant (disabled/secondary)
<AIIcon variant="muted" className="w-4 h-4" />

// In a button
<Button>
  <AIIcon className="w-4 h-4 mr-2" />
  AI Research
</Button>

// Ghost button with hover effect
<Button variant="ghost" className="hover:bg-accent-50">
  <AIIcon className="w-4 h-4" />
</Button>`}
          </CodeBlock>
        </div>

        {/* Avatars */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Avatars</h3>
          <Stack spacing={4}>
            <div className="flex items-center gap-3">
              <Avatar size="sm" initials="JD" name="John Doe" />
              <Avatar size="md" initials="AB" name="Alice Brown" />
              <Avatar size="lg" initials="CS" name="Charlie Smith" />
              <Avatar size="xl" src="https://i.pravatar.cc/150?img=1" name="Sarah Wilson" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Avatar Group</p>
              <AvatarGroup max={3}>
                <Avatar initials="JD" name="John Doe" />
                <Avatar initials="AB" name="Alice Brown" />
                <Avatar initials="CS" name="Charlie Smith" />
                <Avatar initials="MJ" name="Mary Johnson" />
                <Avatar initials="RW" name="Robert Wilson" />
              </AvatarGroup>
            </div>
          </Stack>
          <CodeBlock>
{`<Avatar size="md" initials="JD" name="John Doe" />
<Avatar size="lg" src="/avatar.jpg" name="Sarah" />

<AvatarGroup max={3}>
  <Avatar initials="JD" name="John Doe" />
  <Avatar initials="AB" name="Alice Brown" />
  <Avatar initials="CS" name="Charlie Smith" />
  <Avatar initials="MJ" name="Mary Johnson" />
</AvatarGroup>`}
          </CodeBlock>
        </div>
    </SectionWrapper>
  );
}
